import {
  Box,
  createStyles,
  IconButton,
  makeStyles,
  Tooltip,
} from '@material-ui/core'
import { AddOutlined } from '@material-ui/icons'
import { WorkingStepIconSvg } from 'components/Common/WorkingSteps/WorkingStepIcon'
import { ActionIconButton } from 'components/Layout/ActionIconButton'
import { useLocalizedWorkingStepLabel } from 'components/Localization/useLocalizedWorkingStepLabel'
import { WorkingStepsDialogProps } from 'components/ProjectPage/AdditionalWorkingSteps/Dialog/WorkingStepsDialog'
import { ProjectEditableStateSelector } from 'features/BillOfMaterials/store/selectors/ProjectEditableStateSelector'
import { useDialogManager } from 'features/DialogManager/DialogManager'
import { BoMItemType } from 'model/Project/BoMItemRow'
import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import ScrollContainer from 'react-indiana-drag-scroll'
import { useAppSelector } from 'store/configureStore'
import {
  WorkingStepDto,
  WorkingStepType,
} from 'services/APIs/InternalAPI/internal-api.contracts'
import { useOrganizationAndProjectContext } from 'utils/useOrganizationContext'
import ResourceSelector from './ResourceSelector'

type BoMItemWorkingStepsProps = {
  rowIds: string[]
  workingSteps: WorkingStepDto[]
  materialId: string
  bomItemType: BoMItemType
  additionalClassNames?: string
  hideShowButton?: boolean
  isEditable: boolean
  showPurchasingWorkingStep?: boolean
  wrap?: boolean
  iconClassName?: string
}

const BoMItemWorkingStepButton = (props: {
  workingStep: WorkingStepDto
  rowId: string
  projectId: string
  organizationId: string
  handleClick: (e, workingStep: WorkingStepDto) => void
  isEditable: boolean
  iconClassName?: string
}) => {
  const [resourceSelectorOpen, setResourceSelectorOpen] = React.useState(false)
  const buttonRef = React.useRef(undefined)
  const { t } = useTranslation()
  const { getLocalizedWorkingStep } = useLocalizedWorkingStepLabel()

  const [, setResourceName] = React.useState(props.workingStep?.resource?.name)

  React.useEffect(() => {
    setResourceName(props.workingStep?.resource?.name)
  }, [props.workingStep?.resource?.name])

  const TooltipText = () => {
    let matchText = getLocalizedWorkingStep(
      props.workingStep.primaryWorkingStep
    )

    if (props.workingStep.resource) {
      matchText += '\n' + t(props.workingStep.resource.matchDescription)
    }

    return <span style={{ whiteSpace: 'pre' }}>{matchText}</span>
  }

  return (
    <>
      <Tooltip title={<TooltipText />} arrow placement={'left'}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          flexDirection="column"
          textAlign="center"
          maxWidth={'12rem'}
          // width="max-content"
          className={props.iconClassName}
        >
          <IconButton
            ref={buttonRef}
            key={`${props.rowId}.${props.workingStep.key}`}
            onClick={(e) => props.handleClick(e, props.workingStep)}
            // onContextMenu={e => handleContextMenu(e)}
            disabled={!props.isEditable}
            // title={props.workingStep.resource ? t(props.workingStep.resource.matchDescription) : ''}
            size="small"
          >
            {/* <Badge variant="dot" color="primary" invisible={!props.workingStep.resource || props.workingStep.resource.matchType <= ResourceMatchType.OneCapableResource}> */}
            <WorkingStepIconSvg
              attributes={{
                width: '2em',
              }}
              disableTitle
              workingStepType={props.workingStep.primaryWorkingStep}
            />

            {/* </Badge> */}
            {/* <Typography variant="body2" color="textSecondary">
              {resourceName}
            </Typography> */}
          </IconButton>
          <ResourceSelector
            organizationId={props.organizationId}
            projectId={props.projectId}
            bomItemId={props.rowId}
            workingStep={props.workingStep}
            onClose={() => setResourceSelectorOpen(false)}
            open={resourceSelectorOpen}
            setResourceName={setResourceName}
            disabled={!props.isEditable}
          />
        </Box>
      </Tooltip>
    </>
  )
}

const useStyles = makeStyles(() =>
  createStyles({
    scrollContainer: {
      display: 'flex',
      alignItems: 'flex-start',
    },
  })
)

export function AddWorkingStepButton(props: {
  bomItemType: BoMItemType
  rowIds: string[]
}) {
  const { openDialog } = useDialogManager()
  const { t } = useTranslation()

  const projectIsEditable = useAppSelector(ProjectEditableStateSelector)

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { partyId, projectId } = useOrganizationAndProjectContext()

  const setWorkingStepsDialogOpen = useCallback(() => {
    openDialog('WorkingSteps', {
      bomItemType: props.bomItemType,
      isActive: true,
      rowIds: props.rowIds,
      activeWorkingStep: undefined,
    } as WorkingStepsDialogProps)
  }, [openDialog, props.bomItemType, props.rowIds])

  if (!projectIsEditable) {
    return null
  }

  return (
    <Tooltip
      title={t('project:working-steps-add-button')}
      arrow
      placement="top"
    >
      <ActionIconButton
        size="small"
        onClick={() => {
          setWorkingStepsDialogOpen()
        }}
      >
        <AddOutlined />
      </ActionIconButton>
    </Tooltip>
  )
}

export const BoMItemWorkingSteps = (props: BoMItemWorkingStepsProps) => {
  const cn = useStyles()
  const { openDialog } = useDialogManager()
  const { partyId, projectId } = useOrganizationAndProjectContext()
  const projectIsEditable = useAppSelector(ProjectEditableStateSelector)

  const setWorkingStepsDialogOpen = useCallback(() => {
    openDialog('WorkingSteps', {
      bomItemType: props.bomItemType,
      isActive: true,
      projectId: projectId,
      rowIds: props.rowIds,
      activeWorkingStep: activeWorkingStep.current,
    } as WorkingStepsDialogProps)
  }, [openDialog, projectId, props.bomItemType, props.rowIds])

  const handleClick = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    ws: WorkingStepDto
  ) => {
    activeWorkingStep.current = ws
    setWorkingStepsDialogOpen()
  }

  const activeWorkingStep = React.useRef(undefined)

  const workingStepButtons = props.workingSteps
    .filter(
      (x) =>
        props.showPurchasingWorkingStep ||
        x.primaryWorkingStep !== WorkingStepType.Purchasing
    )
    .map((ws) => {
      return (
        <BoMItemWorkingStepButton
          key={ws.key}
          handleClick={handleClick}
          isEditable={props.isEditable ?? projectIsEditable}
          organizationId={partyId}
          projectId={projectId}
          rowId={props.rowIds[0]}
          workingStep={ws}
          iconClassName={props.iconClassName}
        />
      )
    })

  return (
    <>
      <div
        className={`row-working-steps--container ${props.additionalClassNames}`}
      >
        {!props.workingSteps?.length ? null : !props.wrap ? (
          <ScrollContainer
            horizontal
            hideScrollbars={false}
            className={cn.scrollContainer}
          >
            {workingStepButtons}
          </ScrollContainer>
        ) : (
          workingStepButtons
        )}
      </div>
      {/* {!Boolean(props.hideShowButton) && props.isEditable && (
        <AddWorkingStepButton
          t={t}
          setWorkingStepsDialogOpen={setWorkingStepsDialogOpen}
          current={activeWorkingStep.current}
        ></AddWorkingStepButton>
      )} */}
    </>
  )
}
